import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    Container, 
    IconButton, 
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useStatus } from '../providers/MsgStatusProvider';
import { usePasswordValidation } from '../hooks/usePasswordValidation';
import PasswordRequirements from '../components/PasswordRequirements';
import { logError, logInfo } from '../utils/logger';

/**
 * PasswordResetConfirm Component
 * Allows users to set a new password using a reset token
 * Now uses reusable password validation components
 * 
 * @component
 */
const PasswordResetConfirm = () => {
    const [formData, setFormData] = useState({
        new_password: '',
        confirm_password: '',
    });
    const [loading, setLoading] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const { token } = useParams();
    const navigate = useNavigate();
    const { setStatusMessage, setIsAlertOpen, setStatusType } = useStatus();

    // Use the reusable password validation hook
    const {
        requirements,
        strength,
        isPasswordValid,
        getRequirementDescriptions
    } = usePasswordValidation(
        formData.new_password,
        formData.confirm_password
    );

    /**
     * Handles form input changes
     * @param {Event} e - Input change event
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    /**
     * Prevents mouse down event on password toggle button
     * @param {Event} event - Mouse down event
     */
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    /**
     * Toggles password visibility for both fields
     */
    const handleHideShowPassword = () => {
        setIsPasswordVisible(!isPasswordVisible);
    };

    /**
     * Handles form submission
     * @param {Event} e - Form submit event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isPasswordValid()) {
            logError('Password reset form validation failed', {
                hasMinLength: requirements.minLength,
                hasComplexity: requirements.hasNumber && requirements.hasUpper && requirements.hasLower,
                passwordsMatch: requirements.passwordsMatch,
                strengthLevel: strength.level
            });
            return;
        }

        setLoading(true);
        
        try {
            logInfo('Password reset confirmation attempted', {
                token: token ? 'present' : 'missing',
                strengthLevel: strength.level
            });

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}user/password/reset/confirm/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    ...formData,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                logInfo('Password reset successful', { token: 'present' });
                setStatusType('success');
                setStatusMessage('Password has been reset successfully.');
                setIsAlertOpen(true);
                navigate('/login');
            } else {
                const errorMessage = data.error || 'Failed to reset password';
                logError('Password reset failed', { 
                    status: response.status,
                    error: errorMessage,
                    token: token ? 'present' : 'missing'
                });
                throw new Error(errorMessage);
            }
        } catch (error) {
            logError('Password reset error', { 
                error: error.message,
                token: token ? 'present' : 'missing'
            });
            setStatusType('error');
            setStatusMessage(error.message);
            setIsAlertOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography component="h1" variant="h5" gutterBottom>
                    Set New Password
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                    {/* New Password Field */}
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="new_password"
                        label="New Password"
                        type={isPasswordVisible ? 'text' : 'password'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleHideShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                        disabled={loading}
                                    >
                                        {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        id="new_password"
                        value={formData.new_password}
                        onChange={handleChange}
                        disabled={loading}
                        error={formData.new_password !== '' && !requirements.minLength}
                        helperText={formData.new_password !== '' && !requirements.minLength ? 'Password is too short' : ''}
                        aria-describedby="password-requirements"
                        inputProps={{
                            maxLength: 128,
                            autoComplete: 'new-password'
                        }}
                    />

                    {/* Password Requirements Display */}
                    {formData.new_password && formData.new_password.length > 0 && (
                        <PasswordRequirements
                            requirements={requirements}
                            strength={strength}
                            getRequirementDescriptions={getRequirementDescriptions}
                            showStrength={true}
                            compact={false}
                            variant="default"
                            id="password-requirements"
                            sx={{ mt: 1, mb: 2 }}
                        />
                    )}

                    {/* Confirm Password Field */}
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirm_password"
                        label="Confirm Password"
                        type={isPasswordVisible ? 'text' : 'password'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleHideShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                        disabled={loading}
                                    >
                                        {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        id="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        disabled={loading}
                        error={formData.confirm_password !== '' && !requirements.passwordsMatch}
                        helperText={
                            formData.confirm_password !== '' && !requirements.passwordsMatch 
                                ? 'Passwords do not match' 
                                : ''
                        }
                        aria-describedby="password-match"
                        inputProps={{
                            maxLength: 128,
                            autoComplete: 'new-password'
                        }}
                    />

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading || !isPasswordValid()}
                        aria-label="Set new password"
                    >
                        {loading ? <CircularProgress size={24} /> : 'Set New Password'}
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default PasswordResetConfirm;